import { apiClient } from '../apiClient';
import type { ApiResponse } from '../http/types';
import type {
  SiteListItem,
  SiteTreeNode,
  SiteDetail,
  OfflineHistoryItem,
  VirtualTenantDetail,
  CreateVirtualTenantRequest,
  UpdateVirtualTenantRequest,
  SiteRegistryStatusRequest,
  SiteOfflineConfigRequest,
  SiteActiveStatusRequest,
  SiteActiveStatusResponse,
  PendingClientsPayload,
} from './siteManagement.types';

export interface VirtualTenantAlarmPathItem {
  id?: number;
  alarmPathId?: number;
  pathToAlarm?: string;
  siteIdentifier?: string;
  isActive?: boolean;
  inheritFromParent?: boolean;
  priority?: number;
  notes?: string;
  virtualTenantId?: number;
  createdAt?: string;
  updatedAt?: string;
}

const base = '/api/SiteManagement';

// Sites
export function listSites(customerId?: number): Promise<ApiResponse<SiteListItem[]>> {
  const cacheBuster = `_t=${Date.now()}`;
  const customerParam = customerId ? `&customerId=${customerId}` : '';
  const url = `${base}/sites?${cacheBuster}${customerParam}`;
  return apiClient.get<SiteListItem[]>(url, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });
}

export async function listSiteTree(customerId?: number): Promise<ApiResponse<SiteTreeNode[]>> {
  // Add a cache-busting query parameter with current timestamp to ensure fresh data
  const cacheBuster = `_t=${Date.now()}`;
  const customerParam = customerId ? `&customerId=${customerId}` : '';
  const url = `${base}/sites/tree?${cacheBuster}${customerParam}`;
  const response = await apiClient.get<SiteTreeNode[]>(url, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });

  // Map nodes recursively to use siteName for title display (fallback to existing title if siteName not available)
  const mapNodeTitles = (nodes: SiteTreeNode[]): SiteTreeNode[] => {
    return nodes.map(node => {
      const updatedNode = { ...node };
      // For sites, prefer siteName (clean name) over title (which may be siteIdentifier)
      if (node.type === 'site') {
        // First try to use siteName if available
        if (node.siteName) {
          updatedNode.title = node.siteName;
        } 
        // If not, try to extract from siteIdentifier (format: UK-TestSite199-113148)
        else if (node.siteIdentifier) {
          const parts = node.siteIdentifier.split('-');
          // If format is Region-Name-Token, extract Name (middle part(s))
          if (parts.length >= 3) {
            // Remove first (region) and last (token) parts, join the rest
            const extractedName = parts.slice(1, -1).join('-');
            updatedNode.title = extractedName;
          }
        }
        // Otherwise try to extract from existing title if it follows the pattern
        else if (node.title && node.title.includes('-')) {
          const parts = node.title.split('-');
          if (parts.length >= 3) {
            const extractedName = parts.slice(1, -1).join('-');
            updatedNode.title = extractedName;
          }
        }
      }
      // Recursively process children
      if (node.children) {
        updatedNode.children = mapNodeTitles(node.children);
      }
      return updatedNode;
    });
  };

  if (response.success && response.data) {
    response.data = mapNodeTitles(response.data);
  }

  return response;
}

export function getSite(siteIdentifierRegistryId: string | number): Promise<ApiResponse<SiteDetail>> {
  return apiClient.get<SiteDetail>(`${base}/sites/${encodeURIComponent(siteIdentifierRegistryId)}`);
}

export function deleteSite(siteHierarchyId: string | number): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`${base}/sites/${encodeURIComponent(siteHierarchyId)}`);
}

// Site unique alarm paths
export interface UniqueAlarmPathItem {
  id: number;
  pathToAlarm: string;
  pathDepth?: number;
  alarmCount?: number;
  lastAlarmDateTime?: string;
}

export interface VirtualTenantAvailablePathItem extends UniqueAlarmPathItem {
  siteIdentifier?: string;
  isAlreadyAssociated?: boolean;
}

export function getUniqueAlarmPaths(siteIdentifierRegistryId: string | number): Promise<ApiResponse<UniqueAlarmPathItem[]>> {
  return apiClient.get<UniqueAlarmPathItem[]>(`${base}/sites/${encodeURIComponent(siteIdentifierRegistryId)}/unique-alarm-paths`)
    .then(res => {
      // Treat 404 (no paths for site) as an empty list rather than an error
      if (!res.success && res.error && res.error.status === 404) {
        return { success: true, data: [] as UniqueAlarmPathItem[] } as ApiResponse<UniqueAlarmPathItem[]>;
      }
      return res;
    })
    .catch(_err => {
      return { success: true, data: [] as UniqueAlarmPathItem[] } as ApiResponse<UniqueAlarmPathItem[]>;
    });
}

// Site registry operations
export function updateSiteRegistryStatus(siteId: string | number, payload: SiteRegistryStatusRequest): Promise<ApiResponse<boolean>> {
  return apiClient.put<boolean>(`${base}/site-registry/${encodeURIComponent(siteId)}/status`, payload);
}

export function updateSiteRegistryOfflineConfig(siteId: string | number, payload: SiteOfflineConfigRequest): Promise<ApiResponse<boolean>> {
  return apiClient.put<boolean>(`${base}/site-registry/${encodeURIComponent(siteId)}/offline-config`, payload);
}

export function getSiteRegistryOfflineHistory(siteId: string | number): Promise<ApiResponse<OfflineHistoryItem[]>> {
  return apiClient.get<OfflineHistoryItem[]>(`${base}/site-registry/${encodeURIComponent(siteId)}/offline-history`);
}

export function assignSiteCategory(siteId: string | number, siteCategoryId: number): Promise<ApiResponse<boolean>> {
  return apiClient.put<boolean>(`${base}/site-registry/${encodeURIComponent(siteId)}/category`, { siteCategoryId });
}

export function removeSiteCategory(siteId: string | number): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`${base}/site-registry/${encodeURIComponent(siteId)}/category`);
}

// Virtual Tenants
export function listVirtualTenants(): Promise<ApiResponse<VirtualTenantDetail[]>> {
  // Add cache-buster to ensure fresh data
  const cacheBuster = `_t=${Date.now()}`;
  return apiClient.get<VirtualTenantDetail[]>(`${base}/virtual-tenants?${cacheBuster}`);
}

export function createVirtualTenant(payload: CreateVirtualTenantRequest): Promise<ApiResponse<VirtualTenantDetail>> {
  return apiClient.post<VirtualTenantDetail>(`${base}/virtual-tenants`, payload)
    .then(res => {
      // If response has no success field but has data, treat as success
      if (!res.success && res.data && !res.error) {
        return { success: true, data: res.data } as ApiResponse<VirtualTenantDetail>;
      }
      return res;
    })
    .catch(err => {
      throw err;
    });
}

export function getVirtualTenantsBySite(siteId: string | number): Promise<ApiResponse<VirtualTenantDetail[]>> {
  const url = `${base}/virtual-tenants/by-site/${encodeURIComponent(siteId)}`;
  return apiClient.get<VirtualTenantDetail[]>(url);
}

export function updateVirtualTenant(id: string | number, payload: UpdateVirtualTenantRequest): Promise<ApiResponse<VirtualTenantDetail>> {
  return apiClient.put<VirtualTenantDetail>(`${base}/virtual-tenants/${encodeURIComponent(id)}`, payload);
}

export function deleteVirtualTenant(id: string | number): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`${base}/virtual-tenants/${encodeURIComponent(id)}`);
}

// Virtual tenant alarm-paths
export function getVirtualTenantAlarmPaths(vtId: string | number): Promise<ApiResponse<VirtualTenantAlarmPathItem[]>> {
  return apiClient.get<VirtualTenantAlarmPathItem[]>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/alarm-paths`);
}

export function getVirtualTenantAvailablePaths(vtId: string | number): Promise<ApiResponse<VirtualTenantAvailablePathItem[]>> {
  return apiClient.get<VirtualTenantAvailablePathItem[]>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/available-paths`)
    .then(res => {
      // Treat 404 (no available paths) as empty list for easier UI handling
      if (!res.success && res.error && res.error.status === 404) {
        return { success: true, data: [] as VirtualTenantAvailablePathItem[] } as ApiResponse<VirtualTenantAvailablePathItem[]>;
      }
      return res;
    })
    .catch(() => {
      return { success: true, data: [] as VirtualTenantAvailablePathItem[] } as ApiResponse<VirtualTenantAvailablePathItem[]>;
    });
}

export function addVirtualTenantAlarmPaths(vtId: string | number, payload: { alarmPathIds: number[]; inheritFromParent?: boolean; notes?: string; }): Promise<ApiResponse<boolean>> {
  return apiClient.post<boolean>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/alarm-paths`, payload);
}

export function updateVirtualTenantAlarmPaths(vtId: string | number, payload: { associations: Array<{ alarmPathId: number; inheritFromParent?: boolean; priority?: number; notes?: string; }> }): Promise<ApiResponse<boolean>> {
  return apiClient.put<boolean>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/alarm-paths`, payload);
}

export function deleteVirtualTenantAlarmPath(vtId: string | number, pathId: string | number): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/alarm-paths/${encodeURIComponent(pathId)}`);
}

export function setVirtualTenantAlarmPathInherit(vtId: string | number, pathId: string | number, payload: { inheritFromParent: boolean }): Promise<ApiResponse<boolean>> {
  return apiClient.patch<boolean>(`${base}/virtual-tenants/${encodeURIComponent(vtId)}/alarm-paths/${encodeURIComponent(pathId)}/inherit`, payload);
}

// Pending Sites
// Simple in-memory cache for pending sites to avoid repeated slow requests
let _pendingSitesCache: SiteListItem[] | null = null;
let _pendingSitesCacheTs = 0;
const PENDING_SITES_CACHE_TTL_MS = 30 * 1000; // 30 seconds

export function listPendingSites(forceRefresh = false): Promise<ApiResponse<SiteListItem[]>> {
  const now = Date.now();
  if (!forceRefresh && _pendingSitesCache && (now - _pendingSitesCacheTs) < PENDING_SITES_CACHE_TTL_MS) {
    return Promise.resolve({ success: true, data: _pendingSitesCache } as ApiResponse<SiteListItem[]>);
  }
  return apiClient.get<SiteListItem[]>(`${base}/pending-sites`)
    .then(res => {
      if (res?.success && Array.isArray(res.data)) {
        _pendingSitesCache = res.data;
        _pendingSitesCacheTs = Date.now();
      }
      return res;
    })
    .catch((err: unknown) => {
      return { success: false, error: { message: (err as Error)?.message || 'Failed to load pending sites' } } as ApiResponse<SiteListItem[]>;
    });
}

export function approvePendingSite(payload: { customerId: number; pendingSiteRequestGuid: string }): Promise<ApiResponse<SiteListItem>> {
  return apiClient.post<SiteListItem>(`${base}/pending-sites/approve`, payload);
}

export function rejectPendingSite(payload: { customerId?: number; pendingSiteRequestGuid: string; reason?: string }): Promise<ApiResponse<SiteListItem>> {
  return apiClient.post<SiteListItem>(`${base}/pending-sites/reject`, payload);
}

// Client approval / token
export function approveClient(payload: { siteGuid: string; approveChange?: boolean; generateToken?: boolean; approvalNotes?: string }): Promise<ApiResponse<SiteListItem>> {
  return apiClient.post<SiteListItem>(`${base}/approve-client`, payload);
}

export function regenerateToken(payload: { siteGuid: string; reason?: string }): Promise<ApiResponse<SiteListItem>> {
  return apiClient.post<SiteListItem>(`${base}/regenerate-onetime-token`, payload);
}

export function createPendingClients(payload: { filterBySiteGuid?: string }): Promise<ApiResponse<PendingClientsPayload>> {
  return apiClient.post<PendingClientsPayload>(`${base}/pending-clients`, payload);
}

// Site active/inactive status
export function setSiteActiveStatus(siteHierarchyId: number, isActive: boolean): Promise<ApiResponse<SiteActiveStatusResponse>> {
  return apiClient.put<SiteActiveStatusResponse>(`${base}/sites/${siteHierarchyId}/active`, { isActive } as SiteActiveStatusRequest);
}

// Enable or disable notifications for a site
export function setSiteNotificationStatus(siteHierarchyId: number, isNotificationEnabled: boolean): Promise<ApiResponse<any>> {
  return apiClient.put<any>(
    `${base}/sites/${siteHierarchyId}/notifications`,
    { isNotificationEnabled }
  );
}
